const REDACTED = 'REDACTED';
const UNREADABLE = '[Unreadable]';

const SENSITIVE_KEY_PATTERNS = [
  /(auth|authorization|token|cookie|secret|password)/i,
  /(credit(card)?|card(number|num)?|cvv|cvc|securitycode|expir(y|ation)|exp(month|year|date)|payment(card)?|iban)/i,
];

const TOKEN_PATTERN =
  /(bearer\s+[a-z0-9\-._~+/]+=*)|(eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/i;

const hasValidLuhnCheck = (digits: string): boolean => {
  let sum = 0;
  let shouldDouble = false;

  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number(digits[index]);

    if (Number.isNaN(digit)) {
      return false;
    }

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

const isCardNumberString = (value: string): boolean => {
  // Avoid redacting phone-like strings that may incidentally pass Luhn.
  if (/[+()]/.test(value)) {
    return false;
  }

  const digits = value.replace(/[^\d]/g, '');

  if (digits.length < 13 || digits.length > 19) {
    return false;
  }

  return hasValidLuhnCheck(digits);
};

const isSensitiveKey = (key: string): boolean =>
  SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key));

const isSensitiveString = (value: string): boolean =>
  TOKEN_PATTERN.test(value) || isCardNumberString(value);

const sanitizeValue = (value: unknown, key = ''): unknown => {
  if (isSensitiveKey(key)) return REDACTED;

  if (typeof value === 'string' && isSensitiveString(value)) {
    return REDACTED;
  }

  return value;
};

const canWriteProperty = (record: object, key: string): boolean => {
  const descriptor = Object.getOwnPropertyDescriptor(record, key);

  if (!descriptor) {
    return true;
  }

  if (descriptor.set) {
    return true;
  }

  return Boolean(descriptor.writable);
};

const safeSet = (
  record: Record<string, unknown>,
  key: string,
  value: unknown
): void => {
  if (!canWriteProperty(record, key)) {
    return;
  }

  try {
    record[key] = value;
  } catch {
    // Ignore write errors on readonly/getter-only third-party objects.
  }
};

// A DOMException keeps `message`/`name`/`code` in an internal slot behind
// prototype getters, so Object.create(prototype) yields an object whose
// `.message` throws "The DOMException.message getter can only be used on
// instances of DOMException" (Safari). Build a real instance instead.
const createShell = (value: object): object => {
  if (typeof DOMException !== 'undefined' && value instanceof DOMException) {
    try {
      return new DOMException(value.message, value.name);
    } catch {
      // Fall back to the prototype clone below.
    }
  }

  return Object.create(Object.getPrototypeOf(value));
};

const deepClone = <T>(
  value: T,
  visited = new WeakMap<object, unknown>()
): T => {
  if (!value || typeof value !== 'object') {
    return value;
  }

  const valueAsObject = value as object;

  if (visited.has(valueAsObject)) {
    return visited.get(valueAsObject) as T;
  }

  const clonedValue = Array.isArray(value) ? [] : createShell(valueAsObject);

  visited.set(valueAsObject, clonedValue);

  for (const key of Reflect.ownKeys(valueAsObject)) {
    const descriptor = Object.getOwnPropertyDescriptor(valueAsObject, key);
    if (!descriptor) {
      continue;
    }

    if ('value' in descriptor) {
      descriptor.value = deepClone(descriptor.value, visited);
      Object.defineProperty(clonedValue, key, descriptor);
      continue;
    }

    // Copying an accessor would run a native getter against the clone and
    // throw "Illegal invocation" (and a non-configurable one could never be
    // replaced later). Read it against the original and store a plain value.
    let resolved: unknown = UNREADABLE;
    try {
      resolved = descriptor.get
        ? deepClone(descriptor.get.call(valueAsObject), visited)
        : undefined;
    } catch {
      // Keep the placeholder: the clone must stay serializable.
    }

    Object.defineProperty(clonedValue, key, {
      value: resolved,
      writable: true,
      enumerable: descriptor.enumerable,
      configurable: true,
    });
  }

  return clonedValue as T;
};

const sanitizeObject = (value: unknown, visited: WeakSet<object>): unknown => {
  if (!value || typeof value !== 'object') {
    return sanitizeValue(value);
  }

  if (visited.has(value as object)) {
    return value;
  }
  visited.add(value as object);

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      const sanitizedValue = sanitizeObject(value[index], visited);
      safeSet(
        value as unknown as Record<string, unknown>,
        String(index),
        sanitizedValue
      );
    }

    return value;
  }

  const record = value as Record<string, unknown>;

  for (const key of Object.keys(record)) {
    let nestedValue: unknown;
    try {
      nestedValue = record[key];
    } catch {
      // The sanitizer must never throw, or it masks the error being logged.
      // `deepClone` already resolves accessors on the clone path; here (the
      // in-place path) the caller's own object is left untouched.
      continue;
    }

    if (isSensitiveKey(key)) {
      if (nestedValue && typeof nestedValue === 'object') {
        sanitizeObject(nestedValue, visited);
      } else {
        safeSet(record, key, REDACTED);
      }
      continue;
    }

    const sanitizedPrimitive = sanitizeValue(nestedValue);

    if (sanitizedPrimitive === REDACTED) {
      safeSet(record, key, REDACTED);
      continue;
    }

    if (nestedValue && typeof nestedValue === 'object') {
      sanitizeObject(nestedValue, visited);
      continue;
    }

    safeSet(record, key, sanitizedPrimitive);
  }

  return record;
};

/**
 * Redacts sensitive data mutating the same payload reference.
 * Useful for ephemeral metadata where cloning overhead is unnecessary.
 */
export const sanitizeSensitiveDataInPlace = <T>(payload: T): T => {
  if (!payload || typeof payload !== 'object') {
    return sanitizeValue(payload) as T;
  }

  const visited = new WeakSet<object>();
  return sanitizeObject(payload, visited) as T;
};

/**
 * Redacts sensitive data from unknown error payloads.
 * It returns a sanitized clone and never mutates the input.
 */
export const removeSensibleDataFromError = <T>(error: T): T => {
  if (!error || typeof error !== 'object') return error;

  const clonedError = deepClone(error);
  const errorAsRecord = clonedError as {
    config?: { data?: unknown };
  };

  const visited = new WeakSet<object>();

  if (typeof errorAsRecord?.config?.data === 'string') {
    try {
      const parsedData = JSON.parse(errorAsRecord.config.data);
      const sanitizedData = sanitizeObject(parsedData, visited);
      safeSet(
        errorAsRecord.config as unknown as Record<string, unknown>,
        'data',
        JSON.stringify(sanitizedData)
      );
    } catch {
      safeSet(
        errorAsRecord.config as unknown as Record<string, unknown>,
        'data',
        sanitizeValue(errorAsRecord.config.data)
      );
    }
  }

  sanitizeObject(clonedError, visited);

  return clonedError;
};
